import React, { useMemo } from 'react';
import { Alert, Card, Col, Descriptions, Row, Space, Statistic, Tag, Typography } from 'antd';
import { CalculatorOutlined, CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { formatAmount, getActualLoanData, resolveEssPayload, toNumber } from './essPayload';

const { Text } = Typography;

interface ESSQualificationAdviceViewProps {
  data: any;
  formikProps?: any;
}

const ESSQualificationAdviceView: React.FC<ESSQualificationAdviceViewProps> = ({ data, formikProps }) => {
  const actualData = getActualLoanData(data, formikProps);

  const essPayload = useMemo(() => resolveEssPayload(actualData), [
    actualData?.essRequestPayload,
    actualData?.formattedEssRequestPayload,
  ]);

  if (!essPayload) {
    return null;
  }

  const loanType = actualData?.loanType || 'NEW';
  const basicSalary = toNumber(essPayload.basicSalary);
  const netSalary = toNumber(essPayload.netSalary);
  const oneThirdAmount = toNumber(essPayload.oneThirdAmount);
  const existingDeductions = toNumber(essPayload.totalEmployeeDeduction);
  const desiredDeductible = toNumber(essPayload.desiredDeductibleAmount || essPayload.deductibleAmount);
  const calculatedInstallment = toNumber(actualData?.newInstallment || actualData?.currentInstallment);
  const requestedAmount = toNumber(essPayload.requestedAmount);
  const tenure = toNumber(essPayload.tenure);
  const takeoverAmount = toNumber(actualData?.payoffAmountToFsp1 || essPayload.takeOverAmount);
  const netToEmployee = toNumber(actualData?.netDisbursementToEmployee || essPayload.requestedAmount);

  const availableDeductionCapacity = Math.max(netSalary - oneThirdAmount, 0);
  const remainingCapacity = Math.max(availableDeductionCapacity - existingDeductions, 0);

  const estimatedInstallment = desiredDeductible > 0
    ? desiredDeductible
    : calculatedInstallment > 0
      ? calculatedInstallment
    : tenure > 0
      ? requestedAmount / tenure
      : 0;

  const installmentSource = desiredDeductible > 0
    ? 'ESS desired deductible amount'
    : calculatedInstallment > 0
      ? 'workflow calculated installment'
      : 'fallback estimate (amount / tenure)';

  const projectedTotalDeductions = existingDeductions + estimatedInstallment;
  const fitsCapacity = estimatedInstallment <= remainingCapacity;
  const respectsOneThirdRule = projectedTotalDeductions <= availableDeductionCapacity;
  const utilizationAfterLoan = availableDeductionCapacity > 0
    ? ((projectedTotalDeductions / availableDeductionCapacity) * 100)
    : 0;

  return (
    <Card
      title={
        <Space>
          <CalculatorOutlined />
          <span>ESS Qualification Advice</span>
        </Space>
      }
      className="mb-4"
    >
      <Alert
        type={fitsCapacity && respectsOneThirdRule ? 'success' : 'warning'}
        showIcon
        icon={fitsCapacity && respectsOneThirdRule ? <CheckCircleOutlined /> : <WarningOutlined />}
        message={fitsCapacity && respectsOneThirdRule
          ? 'Applicant appears to fit current ESS affordability checks'
          : 'Applicant needs careful review against affordability limits'}
        description="This is advisory based on ESS payload. Final affordability should be confirmed using the generated loan schedule and policy checks."
        className="mb-4"
      />

      <Row gutter={[16, 16]} className="mb-4">
        <Col xs={24} sm={12} md={6}>
          <Statistic title="Net Salary" value={netSalary} precision={2} prefix="TZS" />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Statistic title="One-Third Reserve" value={oneThirdAmount} precision={2} prefix="TZS" />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Statistic title="Remaining Deduction Capacity" value={remainingCapacity} precision={2} prefix="TZS" />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Statistic title="Estimated New Installment" value={estimatedInstallment} precision={2} prefix="TZS" />
        </Col>
      </Row>

      <Descriptions bordered column={{ xs: 1, sm: 2, md: 2 }}>
        <Descriptions.Item label="Loan Type">
          <Tag color="blue">{loanType}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Requested Amount">
          <strong>TZS {formatAmount(requestedAmount)}</strong>
        </Descriptions.Item>
        <Descriptions.Item label="Installment Source">
          {installmentSource}
        </Descriptions.Item>
        <Descriptions.Item label="Tenure">
          {tenure > 0 ? `${tenure} months` : 'N/A'}
        </Descriptions.Item>
        <Descriptions.Item label="Projected Deduction Utilization">
          <Tag color={utilizationAfterLoan > 90 ? 'red' : utilizationAfterLoan > 75 ? 'orange' : 'green'}>
            {utilizationAfterLoan.toFixed(1)}%
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Within Remaining Capacity">
          <Tag color={fitsCapacity ? 'green' : 'red'}>{fitsCapacity ? 'YES' : 'NO'}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Respects One-Third Rule">
          <Tag color={respectsOneThirdRule ? 'green' : 'red'}>{respectsOneThirdRule ? 'YES' : 'NO'}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Current Deductions">
          TZS {formatAmount(existingDeductions)}
        </Descriptions.Item>
        <Descriptions.Item label="Deduction Capacity (Net - One Third)">
          TZS {formatAmount(availableDeductionCapacity)}
        </Descriptions.Item>
      </Descriptions>

      {loanType === 'TAKEOVER' && (
        <Alert
          className="mt-4"
          type="info"
          showIcon
          message="Takeover-specific financial picture"
          description={
            <Space direction="vertical" size={0}>
              <Text>Payoff to previous FSP: <strong>TZS {formatAmount(takeoverAmount)}</strong></Text>
              <Text>Net amount to employee: <strong>TZS {formatAmount(netToEmployee)}</strong></Text>
            </Space>
          }
        />
      )}

      {basicSalary > 0 && (
        <Text type="secondary" className="mt-3" style={{ display: 'block' }}>
          Salary reference: Basic salary is TZS {formatAmount(basicSalary)} and net salary is TZS {formatAmount(netSalary)}.
        </Text>
      )}
    </Card>
  );
};

export default ESSQualificationAdviceView;
